import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';

export type DocumentInputType = 'auto' | 'cpf' | 'cnpj';

/** Resolved tax ID kind from field value (`null` when empty). */
export type DetectedDocumentKind = 'cpf' | 'cnpj';

export function validateCPFOrCNPJ(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value;

  if (!value) {
    return null; // No value provided, let `required` handle this case.
  }

  const cleanedValue = value.replace(/\D/g, ''); // Remove non-numeric characters

  if (cleanedValue.length === 11) {
    return isValidCPF(cleanedValue) ? null : {invalidCPF: true};
  }

  if (cleanedValue.length === 14) {
    return isValidCNPJ(cleanedValue) ? null : {invalidCNPJ: true};
  }

  return {invalidDocument: true}; // Neither CPF nor CNPJ
}

/** Validates CPF, CNPJ, or both depending on {@link DocumentInputType}. */
export function validateDocumentByType(type: DocumentInputType): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (type === 'auto') {
      return validateCPFOrCNPJ(control);
    }

    const value = control.value;
    if (!value) {
      return null;
    }

    const cleanedValue = String(value).replace(/\D/g, '');

    if (type === 'cpf') {
      if (cleanedValue.length !== 11) {
        return {invalidDocument: true};
      }
      return isValidCPF(cleanedValue) ? null : {invalidCPF: true};
    }

    if (cleanedValue.length !== 14) {
      return {invalidDocument: true};
    }
    return isValidCNPJ(cleanedValue) ? null : {invalidCNPJ: true};
  };
}

export function isValidCPF(cpf: string): boolean {
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

  const calcCheckDigit = (factor: number, max: number) =>
    cpf
      .substring(0, max)
      .split('')
      .reduce((sum, num, index) => sum + parseInt(num) * (factor - index), 0) %
    11;

  const checkDigit1 = calcCheckDigit(10, 9);
  const checkDigit2 = calcCheckDigit(11, 10);

  return (
    parseInt(cpf[9]) === (checkDigit1 < 2 ? 0 : 11 - checkDigit1) &&
    parseInt(cpf[10]) === (checkDigit2 < 2 ? 0 : 11 - checkDigit2)
  );
}

export function isValidCNPJ(cnpj: string): boolean {
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;

  const calcCheckDigit = (cnpj: string, weights: number[]) =>
    cnpj
      .substring(0, weights.length)
      .split('')
      .reduce((sum, num, index) => sum + parseInt(num) * weights[index], 0) %
    11;

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, ...weights1];

  const checkDigit1 = calcCheckDigit(cnpj, weights1);
  const checkDigit2 = calcCheckDigit(cnpj, weights2);

  return (
    parseInt(cnpj[12]) === (checkDigit1 < 2 ? 0 : 11 - checkDigit1) &&
    parseInt(cnpj[13]) === (checkDigit2 < 2 ? 0 : 11 - checkDigit2)
  );
}
